'use client'

import type { ReactNode } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'

import { useQuery } from '@tanstack/react-query'
import { formatCss, oklch, parse } from 'culori'
import { parseCookies, setCookie } from 'nookies'

import {
  MAX_AGE_COOKIE,
  SETTINGS_DATA_COOKIE,
  TENANT_DATA_COOKIE,
} from '@/lib/utils'

import { PageLoading } from '@/components/page-loading'

import type { SettingsActions } from './actions'
import { settingCustomizeAction, settingSetTenantAction } from './actions'
import reducer, { initialState } from './reducer'

import { fetchSettingsService } from '@/services/setting'
import type { Domain } from '@/types/domain'
import type { Customize } from '@/types/settings'

export type ContextDataType = {
  settings: typeof initialState
  dispatch: React.Dispatch<SettingsActions>
}

export const SettingsContext = createContext({} as ContextDataType)

function updateColorDocumentRoot(settings: Customize) {
  const { style } = document.documentElement

  const colorMap: Record<string, keyof Customize> = {
    '--custom-background': 'color_background',
    '--content': 'color_content',
    '--sidebar': 'color_sidebar',
    '--header': 'color_header',
    '--primary': 'color_primary',
    '--sidebar-accent': 'color_primary',
    '--secondary': 'color_secondary',
    '--info': 'color_info',
    '--success': 'color_success',
    '--warning': 'color_warning',
    '--destructive': 'color_danger',
    '--review': 'color_review',
    '--recap-review': 'color_recap_review',
    '--question': 'color_question',
    '--theory': 'color_theory',
    '--summary': 'color_summary',
    '--fixed': 'color_fixed',
    '--extra': 'color_extra',
    '--dry-law': 'color_dry_law',
    '--file-notes': 'color_file_notes',
    '--flashcards': 'color_flashcards',
    '--jurisprudence': 'color_jurisprudence',
    '--mind-map': 'color_mind_map',
    '--mnemonic': 'color_mnemonic',
  }

  for (const [cssVar, settingKey] of Object.entries(colorMap)) {
    const inputColor = settings[settingKey]
    const parsed =
      inputColor && typeof inputColor === 'string' ? parse(inputColor) : null

    if (parsed) {
      const oklchColor = oklch(parsed)
      if (oklchColor) {
        style.setProperty(cssVar, formatCss(oklchColor))
      }
    }
  }
}

const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const cookiesInstance = parseCookies()

  const { isSuccess, data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettingsService,
    refetchInterval: 30 * 1000,
  })

  const [state, action] = useReducer(reducer, initialState)

  const [settings, dispatch] = useMemo(() => [state, action], [state, action])

  useEffect(() => {
    if (!isSuccess || !data?.data) return

    updateColorDocumentRoot(data?.data)
    dispatch(settingCustomizeAction(data?.data))
    setCookie(null, SETTINGS_DATA_COOKIE, JSON.stringify(data?.data), {
      maxAge: MAX_AGE_COOKIE,
      httpOnly: false,
      path: '/',
      sameSite: 'strict',
    })
  }, [cookiesInstance, data?.data, dispatch, isSuccess])

  useEffect(() => {
    if (!settings.tenant && cookiesInstance[TENANT_DATA_COOKIE]) {
      const storagedTenant = cookiesInstance[TENANT_DATA_COOKIE] as string
      const tenant = JSON.parse(storagedTenant) as Domain
      dispatch(settingSetTenantAction(tenant))
    }
  }, [cookiesInstance, dispatch, settings.tenant])

  return (
    <SettingsContext.Provider value={{ settings, dispatch }}>
      {children}

      {isLoading ? <PageLoading /> : null}
    </SettingsContext.Provider>
  )
}

export const useSettings = () => {
  const context = useContext(SettingsContext)

  if (!context) {
    throw new Error('useSettings must be used within an SettingsProvider.')
  }

  return context
}

export default SettingsProvider
